// packages/api/src/writeTimeoutSignal.ts
//
// ROADMAP #131 — "A WRITE TIMED OUT" IS ONE SIGNAL, AND THE LISTS ON SCREEN RE-READ ON IT — WHEN NOTHING IS IN FRONT OF
// THEM.
//
// ⚠⚠ WHY. A save the client stopped waiting for may still have been applied (#122 says so in its message). The list behind
// the drawer kept what it had, so a save the server DID finish never showed, and the obvious retry made a duplicate. The
// API layer cannot know which list a save belongs to, so it announces the timeout and every list re-reads itself (apps/app
// `useRefreshOnWriteTimeout`) — then the host can see whether it landed.
//
// ⚠⚠ NOT WHILE A DRAWER IS OPEN (#131 review). Re-reading at once cleared the very message the drawer was showing (a list
// read clears its hook's last error, which 21 edit drawers render), and could re-seed a form being edited. So the app
// registers a GATE (no drawer / modal open): a timeout marks the lists stale, and they re-read when the gate is clear —
// the moment the host closes the drawer and looks at the list.
// ⚠ And ONCE per burst: a bulk action whose requests time out one after another announces many times; the re-read is
// coalesced, never one per request.
// ⚠ And the host is TOLD, once, when the lists re-read (`onWriteTimeoutReread` — apps/app shows a notice): a list read
// clears the hook's last error, so on a page with no drawer open (an inline reorder, a bulk check-in) the timeout's own
// message vanished within a tick, and a bulk action confirmed in a modal never showed it at all (#131 second review).
// A READ that timed out changes nothing, so it does not announce.

type WriteTimeoutListener = () => void;

/** When lists may re-read: `isClear` now, and a way to hear when that may have changed. */
export interface WriteTimeoutGate {
  isClear: () => boolean;
  onChange: (listener: () => void) => () => void;
}

const listeners = new Set<WriteTimeoutListener>();
const rereadListeners = new Set<WriteTimeoutListener>();
let pending = false;
let scheduled = false;
let gate: WriteTimeoutGate | null = null;
let gateOff: (() => void) | null = null;

/** Re-read every list that asked, once, if a write timed out and nothing is in front of them. */
const flushIfDue = (): void => {
  if (!pending || (gate && !gate.isClear())) {
    return;
  }

  pending = false;
  [...listeners, ...rereadListeners].forEach((listener) => {
    try {
      listener();
    } catch {
      // One list failing to re-read must not stop the others (or the notice).
    }
  });
};

/** Re-read on a write that timed out (when the gate is clear). Returns the unsubscribe. */
export const onWriteTimeout = (listener: WriteTimeoutListener): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/** Be told once per re-read after a timed-out write (to say so to the host). Returns the unsubscribe. */
export const onWriteTimeoutReread = (listener: WriteTimeoutListener): (() => void) => {
  rereadListeners.add(listener);

  return () => {
    rereadListeners.delete(listener);
  };
};

/** Announce a write that timed out (ApiService does; so does a REST upload that stalled). Coalesced to one re-read. */
export const notifyWriteTimeout = (): void => {
  pending = true;

  if (scheduled) {
    return;
  }

  scheduled = true;
  setTimeout(() => {
    scheduled = false;
    flushIfDue();
  }, 0);
};

/** The app says when lists may re-read (apps/app: when no drawer / modal is open). `null` — always. */
export const setWriteTimeoutGate = (next: WriteTimeoutGate | null): void => {
  gateOff?.();
  gate = next;
  gateOff = next ? next.onChange(flushIfDue) : null;
};
